/**
 * VAT Number Normalizer
 *
 * Handles intelligent parsing and conversion of Finnish and Swedish organization numbers
 * to proper VAT format.
 */

/**
 * Normalize Finnish Business ID to VAT format
 *
 * Finnish Business ID format: 1234567-8 (7 digits, hyphen, 1 check digit)
 * Finnish VAT format: FI12345678 (FI prefix + 8 digits, no hyphen)
 *
 * @param {string} input - Finnish Business ID
 * @returns {string|null} - Normalized VAT number or null if invalid
 */
const normalizeFinnishBusinessId = (input) => {
    // Remove whitespace
    const cleaned = input.trim().replace(/\s/g, '');

    // Match Finnish Business ID format: 1234567-8
    const finnishIdPattern = /^(\d{7})-?(\d)$/;
    const match = cleaned.match(finnishIdPattern);

    if (match) {
        const [, digits, checkDigit] = match;
        return `FI${digits}${checkDigit}`;
    }

    return null;
};

/**
 * Normalize Swedish Organization Number to VAT format
 *
 * Swedish Organization Number format: 556xxx-xxxx (10 digits with optional hyphen after 6th digit)
 * Swedish VAT format: SE556xxxxxxxxx01 (SE prefix + 10 digits + 01 suffix)
 *
 * Note: Swedish VAT numbers always end with "01" for regular companies
 *
 * @param {string} input - Swedish Organization Number
 * @returns {string|null} - Normalized VAT number or null if invalid
 */
const normalizeSwedishOrgNumber = (input) => {
    // Remove whitespace
    const cleaned = input.trim().replace(/\s/g, '');

    // Match Swedish Organization Number format: 556xxx-xxxx or 556xxxxxxx
    const swedishOrgPattern = /^(\d{6})-?(\d{4})$/;
    const match = cleaned.match(swedishOrgPattern);

    if (match) {
        const [, first6, last4] = match;
        return `SE${first6}${last4}01`;
    }

    return null;
};

/**
 * Detect and normalize VAT/Organization number
 *
 * Automatically detects:
 * - Finnish Business IDs (1234567-8) -> FI12345678
 * - Swedish Organization Numbers (556xxx-xxxx) -> SE556xxxxxxxxx01
 * - Already formatted VAT numbers (FI12345678, SE556xxxxxxxxx01) -> unchanged
 * - Other EU VAT formats -> unchanged
 *
 * @param {string} input - User input
 * @returns {Object} - { normalized: string, detected: string, original: string }
 */
export const normalizeVatNumber = (input) => {
    if (!input || typeof input !== 'string') {
        return {
            normalized: '',
            detected: 'unknown',
            original: input || ''
        };
    }

    const trimmed = input.trim().toUpperCase();

    // Already has country prefix - assume it's a VAT number
    if (/^[A-Z]{2}/.test(trimmed)) {
        return {
            normalized: trimmed.replace(/\s/g, ''),
            detected: 'vat',
            original: input
        };
    }

    // Try Finnish Business ID normalization
    const finnishVat = normalizeFinnishBusinessId(input);
    if (finnishVat) {
        return {
            normalized: finnishVat,
            detected: 'finnish-business-id',
            original: input
        };
    }

    // Try Swedish Organization Number normalization
    const swedishVat = normalizeSwedishOrgNumber(input);
    if (swedishVat) {
        return {
            normalized: swedishVat,
            detected: 'swedish-org-number',
            original: input
        };
    }

    // Unknown format - return as-is (uppercase, no spaces)
    return {
        normalized: trimmed.replace(/\s/g, ''),
        detected: 'unknown',
        original: input
    };
};

/**
 * Format display value for VAT input
 * Shows user-friendly format in the input field
 *
 * @param {string} input - Current input value
 * @returns {string} - Formatted display value
 */
export const formatVatDisplay = (input) => {
    if (!input) return '';

    const trimmed = input.trim().toUpperCase();

    // If it looks like a Finnish Business ID (7-8 digits, possibly with hyphen)
    const finnishPattern = /^(\d{7})-?(\d?)$/;
    const finnishMatch = trimmed.match(finnishPattern);
    if (finnishMatch) {
        const [, digits, checkDigit] = finnishMatch;
        return checkDigit ? `${digits}-${checkDigit}` : digits;
    }

    // If it looks like a Swedish Org Number (10 digits, possibly with hyphen)
    const swedishPattern = /^(\d{6})-?(\d{0,4})$/;
    const swedishMatch = trimmed.match(swedishPattern);
    if (swedishMatch) {
        const [, first6, last4] = swedishMatch;
        return last4 ? `${first6}-${last4}` : first6;
    }

    // Return as-is for other formats
    return trimmed;
};

/**
 * Get helpful hint text based on detected format
 *
 * @param {string} detected - Detected format type
 * @param {string} t - Translation function
 * @returns {string} - Helpful hint text
 */
export const getVatHintText = (detected, t) => {
    switch (detected) {
        case 'finnish-business-id':
            return t('onboarding.vatHint.finnish') || 'Finnish Business ID detected - will be converted to VAT format';
        case 'swedish-org-number':
            return t('onboarding.vatHint.swedish') || 'Swedish Organization Number detected - will be converted to VAT format';
        case 'vat':
            return t('onboarding.vatHint.vat') || 'VAT number format detected';
        default:
            return t('onboarding.vatHelp') || 'Enter your VAT number or organization number';
    }
};

/**
 * Validate input format (not full VAT validation, just format check)
 *
 * @param {string} input - User input
 * @returns {boolean} - True if format looks reasonable
 */
export const isValidVatFormat = (input) => {
    if (!input || input.length < 8) return false;

    const result = normalizeVatNumber(input);

    // Accept if we detected a known format
    if (result.detected !== 'unknown') return true;

    // Otherwise, check if it's at least alphanumeric and reasonable length
    const cleaned = result.normalized;
    return /^[A-Z]{2}[\dA-Z]{6,12}$/.test(cleaned);
};

/**
 * Convert Finnish VAT to Finnish business ID (Y-tunnus).
 *
 * FI VAT format: FI12345678
 * Y-tunnus format: 1234567-8
 *
 * Returns null if input is not a Finnish VAT/business-id format.
 *
 * @param {string} input
 * @returns {string|null}
 */
export const toFinnishBusinessId = (input) => {
    if (!input || typeof input !== 'string') return null;

    const cleaned = input.trim().toUpperCase().replace(/\s/g, '');
    const vatMatch = cleaned.match(/^FI(\d{7})(\d)$/);
    if (vatMatch) {
        return `${vatMatch[1]}-${vatMatch[2]}`;
    }

    const businessIdMatch = cleaned.match(/^(\d{7})-?(\d)$/);
    if (businessIdMatch) {
        return `${businessIdMatch[1]}-${businessIdMatch[2]}`;
    }

    return null;
};
